import { useKindeAuth } from '@kinde-oss/kinde-auth-react'
import { type ReactNode, useEffect, useState } from 'react'

import type { OrganizationFeatureToggles } from '@ankor-io/common/types/system'

import { useUserContext } from '@/providers/user/useUserContext'

import { FeatureToggleContext } from './FeatureToggleContext'
import type { FeatureToggleContextType } from './types'

export function FeatureToggleProvider({ children }: { children: ReactNode }) {
  const [orgFeatureToggles, setOrgFeatureToggles] = useState<OrganizationFeatureToggles | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const { activeOrganizationId } = useUserContext()
  const { getAccessToken } = useKindeAuth()

  const fetchFeatureToggles = async (orgId: string): Promise<OrganizationFeatureToggles | null> => {
    try {
      const res = await fetch(`/api/pay/org/${orgId}/feature-toggles`, {
        headers: {
          Authorization: `Bearer ${await getAccessToken()}`,
        },
      })

      if (!res.ok) {
        throw new Error('Failed to fetch feature toggles')
      }

      const data = (await res.json()) as OrganizationFeatureToggles
      return data
    } catch (error) {
      console.error('Error fetching feature toggles:', error)
      return null
    }
  }

  const refreshFeatureToggles = async () => {
    if (!activeOrganizationId) {
      return
    }

    try {
      const toggles = await fetchFeatureToggles(activeOrganizationId)
      setOrgFeatureToggles(toggles)
    } catch (error) {
      console.error('Error refreshing feature toggles:', error)
    }
  }

  useEffect(() => {
    const loadFeatureToggles = async () => {
      if (!activeOrganizationId) {
        setOrgFeatureToggles(null)
        setIsLoading(false)
        return
      }

      setIsLoading(true)

      try {
        const toggles = await fetchFeatureToggles(activeOrganizationId)
        setOrgFeatureToggles(toggles)
      } catch (error) {
        console.error('Error loading feature toggles:', error)
      } finally {
        setIsLoading(false)
      }
    }

    loadFeatureToggles()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- we only want to fetch feature toggles when the active organization changes
  }, [activeOrganizationId])

  const contextValue: FeatureToggleContextType = {
    orgFeatureToggles,
    isLoading,
    refreshFeatureToggles,
  }

  return <FeatureToggleContext.Provider value={contextValue}>{children}</FeatureToggleContext.Provider>
}
